import { ONE_MB } from "app/appConstants";
import axios, { AxiosInstance, AxiosPromise, AxiosRequestConfig, AxiosResponse } from "axios";
import { ImageUtils } from "components/MprcImageHandler/ImageResizer";
import { AsyncUploadResult } from "generated/CoreApi_v1";
import { SessionStore } from "services/SessionStore";
import { AuthService, TokenRenew } from "services/authService";
import { SessionService } from "services/sessionService";
import { v4 } from 'uuid';
import { FileToUpload } from "./MprcImageHandler";
////////////////////////////////////////////////////////////////////////////////

interface AsyncUploadedEvents {
  onFileStarted?: () => void;
  onFileFinished?: () => void;
  onFileUploadSuccess?: (data: AsyncUploadResult) => void;
  onFileUploadError?: (instance: AsyncUploader, data: any) => void;
  onFileUploadProgress?: (progress: number) => void;
}
interface AsyncUploaderProps extends AsyncUploadedEvents {
  ignoreStorageLimit?: boolean;
}

type UploadChunkedResult = {
  error: string | null;
  fileUploaded?: AsyncUploadResult;
};

////////////////////////////////////////////////////////////////////////////////

export class AsyncUploader {
  private props: AsyncUploaderProps;
  private abortController?: AbortController;
  private ax: AxiosInstance;
  private interceptor: number | null = null;

  constructor(props: AsyncUploaderProps) {
    this.props = props;
    this.ax = this.createAxiosInstance();
  }

  [Symbol.dispose]() {
    if (this.interceptor != null) this.ax.interceptors.response.eject(this.interceptor);
  }

  private createAxiosInstance() {

    const ax = axios.create();

    this.interceptor = ax.interceptors.response.use(
      res => {
        if (res && res.status === 204) {
          res.data = null;
        }
        return res;
      },
      error => {
        if (axios.isCancel(error)) {
          (error as any)["canceled"] = true;
        }
        else if (axios.isAxiosError(error)) {

          const response = error.response as AxiosResponse;
          const apiException = response && response.data ? (response.data as ApiProblemDetails) : null;
          const authErrors = response && response.headers ? response.headers["www-authenticate"] as string : undefined;
          const originalRequest = error.config!;

          if (
            response
            && response.status === 401
            && authErrors !== undefined
            && (authErrors.indexOf("invalid_token") > -1 || authErrors.indexOf("Bearer") > -1 || authErrors.indexOf("ClaimPrincipal_NOT_VALID") > -1)) {

            const tokenRenewInstance = new TokenRenew();

            return AuthService.updateToken(tokenRenewInstance)
              .then((token) => {
                if (token != null) {
                  originalRequest.headers.setAuthorization(`${token.token_type} ${token.access_token}`);
                  return ax.request(originalRequest);
                }
                else {
                  console.info("Login Expired while refreshing token.");
                }
              });
          }

          if (apiException != null) {
            error = apiException;
          }
          else
            error = {
              message: error.message,
              stack: error.stack,
              name: `Erro ${error.status}`
            } as Error;
        }

        return Promise.reject(error);
      });

    return ax;
  }

  public setEvents(e: AsyncUploadedEvents) {
    this.props.onFileFinished = e.onFileFinished;
    this.props.onFileStarted = e.onFileStarted;
    this.props.onFileUploadError = e.onFileUploadError;
    this.props.onFileUploadProgress = e.onFileUploadProgress;
    this.props.onFileUploadSuccess = e.onFileUploadSuccess;
  }

  private buildUrl(url: string) {
    return SessionService.current.apiPaths.coreApi + url;
  }

  private getAxiosHeaders() {
    const token = SessionStore.bearerString();
    const headers: { [key: string]: string; } = {};
    headers["Accept"] = "application/json";
    headers["Content-Type"] = "multipart/form-data";
    if (token != "") headers["Authorization"] = token;
    return headers;
  }

  private async checkStorageUsage(): Promise<void> {

    if (this.props.ignoreStorageLimit == true || true) return;



  }

  //
  private callServer(url: string, formData: FormData): AxiosPromise<AsyncUploadResult> {
    this.abortController = new AbortController();

    return this.ax.post<AsyncUploadResult>(url, formData, {
      headers: this.getAxiosHeaders(),
      signal: this.abortController?.signal,
      onUploadProgress: (progressEvent) => {
        if (this.props.onFileUploadProgress) {
          var p = progressEvent.total == undefined || progressEvent.total == 0 ? 0 : (Math.ceil(progressEvent.loaded / progressEvent.total * 100));
          this.props.onFileUploadProgress(p);
        }
      },
    });
  }

  //
  public delete(ids: string[]): AxiosPromise {
    const formData = new FormData();
    formData.append('filesToRemove', ids.join(","));

    return this.ax.post(this.buildUrl("/api/v1.0/AsyncUpload/Remove"), formData, {
      headers: this.getAxiosHeaders(),
    });
  }

  //
  public isCancel(thrown: any): boolean {
    return axios.isCancel(thrown) || thrown?.canceled === true;
  }

  public cancel() {
    this.abortController?.abort();
  }

  /**
   * Upload Image.
   * @param file
   * @param autoCreateThumbWithAspectRatio
   * @returns
   */
  public async uploadImage(file: FileToUpload, autoCreateThumbWithAspectRatio: boolean = false): Promise<void> {

    if (file == null || file.sourceDataOrUrl == null)
      return;

    try {
      await this.checkStorageUsage();

      const sourceDataOrUrl = file.sourceDataOrUrl;

      new Promise<FormData>((resolve) => {
        // Obtem os dados para envio.
        const formData = new FormData();
        if (sourceDataOrUrl.indexOf("http") == 0) {
          formData.append('srcUrl', sourceDataOrUrl);
          formData.append('srcName', file.name);
          resolve(formData);
        }
        else if (sourceDataOrUrl.indexOf("blob") == 0) {
          ImageUtils.blobUrlToBase64(sourceDataOrUrl).then((data) => {
            formData.append('file', ImageUtils.imgBase64ToBlob(data, file.contentType), file.name);
            resolve(formData);
          });
        }
        else {
          formData.append('file', ImageUtils.imgBase64ToFile(sourceDataOrUrl, file.name, file.contentType), file.name);
          resolve(formData);
        }
      })
        .then((formData) => {
          if (autoCreateThumbWithAspectRatio) formData.append("createThumbnail", "1");
          this.uploadForm(formData);
        })
        .catch((error) => {
          if (this.props.onFileUploadError)
            this.props.onFileUploadError(this, error);
        });
    } catch (error) {
      if (this.props.onFileUploadError)
        this.props.onFileUploadError(this, error);
    }
  }

  /**
   * Upload a single file.
   * @param file
   * @param otherParameters
   */
  public async uploadSingleFile(file: File, otherParameters?: Record<string, string>): Promise<void> {
    try {
      await this.checkStorageUsage();

      if (file.size < 10 * ONE_MB) {
        this.uploadFile(file, otherParameters);
      }
      else {
        await this.uploadChunked(file, otherParameters);
      }
    } catch (error) {
      if (this.props.onFileUploadError)
        this.props.onFileUploadError(this, error);
    }
  }

  /**
    * Upload a file
    * @param file The file to upload
    * @returns Promise with the final server response
   */
  private uploadFile(file: File, otherParameters?: Record<string, string>): void {

    if (file == null)
      return;

    // Obtem os dados para envio.
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);
      if (otherParameters != null)
        Object.entries(otherParameters).map(([key, value]) => formData.append(key, value));

      this.uploadForm(formData);
    }
    catch (error: any) {
      if (this.props.onFileUploadError)
        this.props.onFileUploadError(this, error);
    }
  }

  /**
   * Upload a file in chunks
   * @param file The file to upload
   * @returns Promise with the final server response
  */
  private async uploadChunked(file: File, otherParameters?: Record<string, string>) {
    const fileId = v4();

    // Automatically adjusts the chunk size to 100 MB or 4000 MB
    // if it determines that the 50,000 block limit will be exceeded.
    // Specifically, if the file size is more than 4 * 50,000 * 1024 * 1024 bytes, the chunk size will be set to 100 MB,
    // and if the file size is more than 100 * 50,000 * 1024 * 1024 bytes, the chunk size will be set to 4000 MB.
    const chunkSize = file.size > 100 * 50000 * ONE_MB
      ? 4000 * ONE_MB
      : file.size > 4 * 50000 * ONE_MB
        ? 100 * ONE_MB
        : 10 * ONE_MB;

    const totalChunks = Math.ceil(file.size / chunkSize);

    this.abortController = new AbortController();

    this.props.onFileStarted?.();

    try {
      let lastResult: UploadChunkedResult | null = null;

      for (let chunkNumber = 0; chunkNumber < totalChunks && this.abortController!.signal.aborted !== true; chunkNumber++) {

        const start = chunkNumber * chunkSize;
        const end = Math.min(file.size, start + chunkSize);
        const chunk = file.slice(start, end);
        const authToken = SessionStore.tokenType() + " " + await SessionService.getAccessToken();

        const formData = new FormData();
        formData.append('file', chunk, file.name);
        formData.append('fileName', file.name);
        formData.append('fileId', fileId);
        formData.append('chunkNumber', chunkNumber.toString());
        formData.append('totalChunks', totalChunks.toString());
        formData.append('chunkSize', chunkSize.toString());
        formData.append('totalSize', file.size.toString());
        formData.append('fileCreation', file.lastModified.toString());
        if (otherParameters != null)
          Object.entries(otherParameters).map(([key, value]) => formData.append(key, value));

        var response = await this.uploadChunk(
          this.buildUrl("/api/v1.0/AsyncUpload/ChunkUpload"),
          formData,
          authToken,
          chunkNumber,
          totalChunks,
          (progressEvent) => {
            const chunkProgress = progressEvent.loaded / progressEvent.total;
            const overallProgress = 100.0 * (chunkNumber + chunkProgress) / totalChunks;
            this.props.onFileUploadProgress?.(Math.ceil(overallProgress));
          }
        );

        lastResult = response.data;

        if (lastResult.error != null) {
          this.props.onFileUploadError?.(this, new Error(lastResult.error));
        }
      }

      if (this.abortController!.signal.aborted === true)
        this.props.onFileUploadError?.(this, "Upload Aborted");
      else if (lastResult?.fileUploaded != null)
        this.props.onFileUploadSuccess?.(lastResult.fileUploaded);
    }
    catch (error: any) {
      this.props.onFileUploadError?.(this, error);
    }

    this.props.onFileFinished?.();
  }

  /**
   * Upload a single chunk
   * @param formData FormData containing the chunk and metadata
   * @param chunkNumber Current chunk number
   * @param totalChunks Total number of chunks
   * @param onUploadProgress Progress callback for this chunk
   * @returns Axios response
   */
  private async uploadChunk(
    uploadUrl: string,
    formData: FormData,
    authToken: string,
    chunkNumber: number,
    totalChunks: number,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<AxiosResponse<UploadChunkedResult>> {
    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
        'X-Chunk-Number': chunkNumber.toString(),
        'X-Total-Chunks': totalChunks.toString(),
        'Authorization': authToken
      },
      signal: this.abortController?.signal,
      onUploadProgress
    };

    // For the last chunk, we might want to finalize the upload
    if (chunkNumber === totalChunks - 1) {
      config.headers!['x-last-chunk'] = 'true';
    }

    try {
      return await this.ax.post(uploadUrl, formData, config);
    } catch (error) {
      // Implement retry logic here if needed
      console.error(`Error uploading chunk ${chunkNumber}:`, error);
      throw error;
    }
  }

  /**
   * Upload ordinary form.
   * @param formData
   */
  private uploadForm(formData: FormData) {
    // Envia para o arquivo.
    if (this.props.onFileStarted)
      this.props.onFileStarted();

    this.callServer(this.buildUrl("/api/v1.0/AsyncUpload/Save"), formData)
      .then((response) => {

        if (response.data.error == null && this.props.onFileUploadSuccess)
          this.props.onFileUploadSuccess(response.data);

        if (response.data.error != null && this.props.onFileUploadError)
          this.props.onFileUploadError(this, new Error(response.data.error));

        if (this.props.onFileFinished)
          this.props.onFileFinished();
      })
      .catch((error: any) => {
        if (this.props.onFileUploadError)
          this.props.onFileUploadError(this, error);

        if (this.props.onFileFinished)
          this.props.onFileFinished();
      });
  }
}
